import { Button } from "../../../ui/Button";
import { COLORS, HUD_HEIGHT, LAYOUT, TEXT_STYLES } from "../../../constants";
import { RunState, levelUpSkill, applyPerk, newRun, getSkillLevel } from "../../../state/runState";
import { CARDS } from "../../../data/cards";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { getAutomationOverride, tagTextForAutomation } from "../../../automation/automationTags";
import { SKILLS } from "../../../data/skills";
import { rollRewardOffer } from "../../../state/rewards";
import { PrimitiveIconId } from "../../../avatars/icons/primitiveIcons";
import { PrimitiveAvatar } from "../../../avatars/PrimitiveAvatar";
import { CHEST_BLUEPRINT } from "../../../avatars/blueprints/chest";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";
import { OverworldState, newOverworldState } from "../../../state/overworldState";
import type { PerkId } from "../../../data/perks";
import { resetOverworldAfterReward } from "../../../state/overworldLogic";
import { navLog } from "../../../debugNavigation";
import type { ScreenContext } from "../../../screens/types";

/**
 * Map perk IDs to icon IDs for display on perk cards.
 */
function getPerkIconId(perkId: string): PrimitiveIconId | undefined {
  // Remove "perk_" prefix if present
  const iconId = perkId.replace(/^perk_/, "") as PrimitiveIconId;
  // Return icon ID if it exists, otherwise undefined (Button handles missing icons gracefully)
  const validIcons: PrimitiveIconId[] = [
    "hp",
    "atk",
    "armor",
    "lifesteal",
    "thorns",
    "rage",
    "adrenaline",
  ];
  return validIcons.includes(iconId) ? iconId : undefined;
}

export class VictoryScreen extends BaseScreen {
  private run!: RunState;
  private chestButton?: Button;
  private skillCard?: Button;
  private chestAvatar?: PrimitiveAvatar;
  private chestPulseTween?: Phaser.Tweens.Tween;
  private chestGlow?: Phaser.GameObjects.Ellipse;
  private overworldState?: OverworldState;

  constructor(scene: EncounterScene) {
    super(scene, "VICTORY");
  }

  enter(context?: ScreenContext) {
    this.run = context?.run ?? newRun();
    this.overworldState = context?.overworldState ?? this.scene.getOverworldState?.();
    const reward = rollRewardOffer(this.run);

    const { width, height } = this.scene.scale;

    // The persistent HUD banner already communicates "Victory".
    const contentTop = Math.max(LAYOUT.screenPadding, HUD_HEIGHT + 180);
    const title = this.scene.add
      .text(width / 2, contentTop, "Choose a reward", TEXT_STYLES.subtitle)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "victory-title");

    const buttonWidth = width - LAYOUT.screenPadding * 2;
    const cardHeight = 110;
    const startY = height * 0.45;

    if (reward.kind === "skill") {
      // Skill reward: show chest avatar and button
      const card = CARDS[reward.cardId];

      // Create chest avatar - positioned higher up
      const chestSize = 120;
      const chestY = height * 0.35;
      const chestX = width / 2;

      this.chestAvatar = new PrimitiveAvatar(this.scene, CHEST_BLUEPRINT, {
        scale: chestSize / CHEST_BLUEPRINT.designSize.w,
      });
      this.chestAvatar.setPosition(chestX, chestY);
      const chestRoot = this.chestAvatar.getRoot();
      chestRoot.setDepth(10); // Ensure chest is above button
      this.addToContainer(chestRoot);

      // Add pulsing glow effect behind the chest
      this.chestGlow = this.scene.add.ellipse(
        chestX,
        chestY,
        chestSize * 1.2,
        chestSize * 1.2,
        COLORS.skillCardStroke,
        0.15
      );
      this.chestGlow.setScrollFactor(0);
      this.chestGlow.setDepth(5); // Behind chest but above button
      this.addToContainer(this.chestGlow);

      // Create button below chest - moved lower to avoid overlap
      const buttonY = height * 0.55;
      this.chestButton = new Button(this.scene, LAYOUT.screenPadding, buttonY, {
        width: buttonWidth,
        height: cardHeight,
        label: "Open",
        description: "Tap to reveal your reward!",
        automationName: "open-chest",
        fillColor: COLORS.skillCardFill,
        strokeColor: COLORS.skillCardStroke,
        badge: {
          text: "Skill",
          fill: COLORS.skillBadgeFill,
        },
        onClick: () => {
          this.revealSkill(card, chestX, chestY, buttonY);
        },
      });
      this.chestButton.setDepth(1); // Button behind chest
      this.addToContainer(this.chestButton);

      // Start pulsing animation
      this.startChestPulse();
    } else {
      // Perk reward: show 3 perk choices (current behavior)
      reward.cardIds.forEach((cardId, index) => {
        const card = CARDS[cardId];
        const perkIconId = getPerkIconId(card.id);
        const button = new Button(
          this.scene,
          LAYOUT.screenPadding,
          startY + index * (cardHeight + LAYOUT.buttonSpacing),
          {
            width: buttonWidth,
            height: cardHeight,
            label: card.name,
            description: card.description,
            automationName: `reward-${card.id}`,
            fillColor: COLORS.perkCardFill,
            strokeColor: COLORS.perkCardStroke,
            badge: {
              text: "Perk",
              fill: COLORS.perkBadgeFill,
            },
            icon: perkIconId,
            onClick: () => {
              // Reward offer guarantees perk cards here.
              applyPerk(this.run, card.id as PerkId);
              this.returnToOverworld();
            },
          }
        );
        this.addToContainer(button);
      });
    }
  }

  private revealSkill(
    card: (typeof CARDS)[keyof typeof CARDS],
    chestX: number,
    chestY: number,
    buttonY: number
  ) {
    if (!card.grantsSkill) return;
    const isAutomationRun = getAutomationOverride("reward") !== undefined;

    // Check if this is a new skill before granting
    const previousLevel = getSkillLevel(this.run, card.grantsSkill);
    const isNewSkill = previousLevel === 0;

    // Grant the skill immediately when chest is opened
    levelUpSkill(this.run, card.grantsSkill);

    const { width } = this.scene.scale;
    const buttonWidth = width - LAYOUT.screenPadding * 2;
    const cardHeight = 110;

    // Stop pulsing animation
    this.chestPulseTween?.stop();

    // Create sparkle effects at chest position
    this.createSparkles(chestX, chestY);

    // Animate chest opening (scale up then fade out)
    if (this.chestAvatar) {
      const chestRoot = this.chestAvatar.getRoot();
      // Scale up chest
      this.scene.tweens.add({
        targets: chestRoot,
        scale: 1.2,
        duration: 150,
        ease: "Power2",
        onComplete: () => {
          // Fade out chest
          this.scene.tweens.add({
            targets: chestRoot,
            alpha: 0,
            scale: 0.8,
            duration: 200,
            ease: "Power2",
            onComplete: () => {
              this.chestAvatar?.destroy();
            },
          });
        },
      });
    }

    // Hide chest button with fade
    if (this.chestButton) {
      this.scene.tweens.add({
        targets: this.chestButton,
        alpha: 0,
        scale: 0.9,
        duration: 200,
        ease: "Power2",
        onComplete: () => {
          this.chestButton?.destroy();
        },
      });
    }

    // Hide glow with fade
    if (this.chestGlow) {
      this.scene.tweens.add({
        targets: this.chestGlow,
        alpha: 0,
        scale: 1.5,
        duration: 250,
        ease: "Power2",
        onComplete: () => {
          this.chestGlow?.destroy();
        },
      });
    }

    // Get the new level after granting
    const newLevel = getSkillLevel(this.run, card.grantsSkill);
    const skill = SKILLS[card.grantsSkill];

    // Create skill card with initial hidden state
    const skillLabel = isNewSkill ? `New Skill: ${skill.name}` : `${skill.name} Level ${newLevel}`;
    const skillDescription = isNewSkill
      ? skill.description
      : `${skill.description}\nUpgraded to level ${newLevel}!`;

    // Create skill card starting at chest position
    // Button's background is centered at (width/2, height/2) relative to container position
    // So to center the button on chest, we need to offset by half the button size
    this.skillCard = new Button(
      this.scene,
      chestX - buttonWidth / 2, // Offset so button center aligns with chest
      chestY - cardHeight / 2, // Offset so button center aligns with chest
      {
        width: buttonWidth,
        height: cardHeight,
        label: skillLabel,
        description: skillDescription,
        automationName: `skill-revealed-${card.id}`,
        fillColor: COLORS.skillCardFill,
        strokeColor: COLORS.skillCardStroke,
        badge: {
          text: isNewSkill ? "NEW!" : `Lv${newLevel}`,
          fill: COLORS.skillBadgeFill,
        },
        onClick: () => {
          // Skill already granted, just proceed
          this.returnToOverworld();
        },
      }
    );
    this.addToContainer(this.skillCard);
    this.skillCard.setDepth(15); // Above chest

    // Start skill card small and invisible (coming out of chest)
    if (isAutomationRun) {
      // Keep automation deterministic/stable: skip motion tween so click targets don't drift.
      this.skillCard.setAlpha(1);
      this.skillCard.setScale(1);
      this.skillCard.setPosition(LAYOUT.screenPadding, buttonY);
      return;
    }

    this.skillCard.setAlpha(0);
    this.skillCard.setScale(0.3);

    // Animate skill card emerging from chest and moving to button position
    this.scene.tweens.add({
      targets: this.skillCard,
      alpha: 1,
      scale: 1,
      x: LAYOUT.screenPadding, // Move to button X position
      y: buttonY, // Move to button Y position
      duration: 400,
      ease: "Cubic.easeOut",
      onComplete: () => {
        if (!this.skillCard) return;
        // Final bounce effect
        this.scene.tweens.add({
          targets: this.skillCard,
          scale: 1.03,
          duration: 110,
          yoyo: true,
          ease: "Sine.easeInOut",
        });
      },
    });
  }

  private startChestPulse() {
    if (!this.chestAvatar || !this.chestGlow) return;

    const chestRoot = this.chestAvatar.getRoot();
    const initialY = chestRoot.y;

    // Pulse the glow
    this.chestPulseTween = this.scene.tweens.add({
      targets: this.chestGlow,
      alpha: { from: 0.15, to: 0.4 },
      scaleX: { from: 1.0, to: 1.2 },
      scaleY: { from: 1.0, to: 1.2 },
      duration: 800,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });

    // Chest avatar pulsing animation (subtle scale)
    this.scene.tweens.add({
      targets: chestRoot,
      scale: { from: 1.0, to: 1.05 },
      duration: 1000,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });

    // Chest floating animation (subtle vertical movement)
    this.scene.tweens.add({
      targets: chestRoot,
      y: { from: initialY, to: initialY - 5 },
      duration: 1200,
      ease: "Sine.easeInOut",
      yoyo: true,
      repeat: -1,
    });
  }

  private createSparkles(centerX: number, centerY: number) {
    const sparkleCount = 8;
    const sparkleColors = [
      COLORS.skillCardStroke,
      COLORS.skillBadgeFill,
      0xffffff,
      0xfde047, // light yellow
    ];

    for (let i = 0; i < sparkleCount; i++) {
      const angle = (i / sparkleCount) * Math.PI * 2;
      const distance = 40 + Math.random() * 30;
      const startX = centerX + Math.cos(angle) * 20;
      const startY = centerY + Math.sin(angle) * 20;
      const endX = centerX + Math.cos(angle) * distance;
      const endY = centerY + Math.sin(angle) * distance;

      const sparkle = this.scene.add.circle(
        startX,
        startY,
        4 + Math.random() * 3,
        sparkleColors[Math.floor(Math.random() * sparkleColors.length)],
        1
      );
      sparkle.setScrollFactor(0);
      this.addToContainer(sparkle);

      // Animate sparkle outward
      this.scene.tweens.add({
        targets: sparkle,
        x: endX,
        y: endY,
        alpha: 0,
        scale: 0,
        duration: 400 + Math.random() * 200,
        ease: "Power2",
        onComplete: () => {
          sparkle.destroy();
        },
      });
    }

    // Add a central burst effect
    const burst = this.scene.add.circle(centerX, centerY, 20, COLORS.skillBadgeFill, 0.6);
    burst.setScrollFactor(0);
    this.addToContainer(burst);

    this.scene.tweens.add({
      targets: burst,
      scale: 2.5,
      alpha: 0,
      duration: 300,
      ease: "Power2",
      onComplete: () => {
        burst.destroy();
      },
    });
  }

  private returnToOverworld() {
    const overworldState = this.overworldState ?? newOverworldState(this.run);
    overworldState.run = this.run;
    resetOverworldAfterReward(overworldState);

    this.scene.overlay.hideBanner();
    this.scene.overlay.transition(() => {
      navLog("VictoryScreen", "Starting overworld after encounter victory.", {
        floor: this.run.floor,
        isBossEncounter: Boolean(this.run.isBossEncounter),
      });
      this.scene.scene.start(OVERWORLD_SCENE_KEY, {
        overworldState,
      });
    });
  }
}
